import React from 'react';

/**
 * Shared pagination component for product lists (search & collection).
 * Handles both page-based and load-more pagination.
 *
 * @param {Object} props
 * @param {number} currentPage
 * @param {number} totalPages
 * @param {boolean} loading
 * @param {function} goToPage
 * @param {function} loadMoreProducts
 * @param {string} paginationType
 * @param {boolean} isActuallyLastPage
 */
export const ProductListPagination = ({
  currentPage,
  totalPages,
  loading,
  goToPage,
  loadMoreProducts,
  paginationType = 'pages',
  isActuallyLastPage = false,
  paginationTextClass = '',
  paginationActiveColour = '', // NEW PROP
  paginationInactiveColour = '' // NEW PROP
}) => {
  if (paginationType === 'pages') {
    return (
      <div className={`flex items-center space-x-2 md:space-x-4 ${paginationTextClass}`}>
        <button
          onClick={() => goToPage(currentPage - 1)}
          disabled={currentPage <= 1 || loading}
          className={` disabled:opacity-50 disabled:cursor-not-allowed ${paginationTextClass}`}
          aria-label='Previous page'
          style={currentPage <= 1 && paginationInactiveColour ? { color: paginationInactiveColour } : undefined}
        >
          <i className='icon icon-chevron-left text-[24px]'></i>
        </button>
        <div className='flex items-center space-x-2 md:space-x-4'>
          {(() => {
            const maxVisiblePages = 3; // Show maximum 3 pages in the middle
            let startPage, endPage;

            if (totalPages <= 5) {
              // Show all pages if total is small
              startPage = 1;
              endPage = totalPages;
            } else {
              // Calculate start and end for sliding window
              const halfWindow = Math.floor(maxVisiblePages / 2);

              if (currentPage <= 2) {
                // Near the beginning, show pages 1-3
                startPage = 1;
                endPage = 3;
              } else if (currentPage === 3) {
                // On page 3, show pages 1-4
                startPage = 1;
                endPage = 4;
              } else if (currentPage === 4) {
                // On page 4, show pages 2-5 to include page 3
                startPage = 2;
                endPage = 5;
              } else if (currentPage >= totalPages - 2) {
                // Near the end, show last 3 pages
                startPage = totalPages - 2;
                endPage = totalPages;
              } else {
                // In the middle, show current page ± 1
                startPage = currentPage - halfWindow;
                endPage = currentPage + halfWindow;
              }
            }

            const allElements: React.ReactNode[] = [];

            // First page and ellipsis
            if (totalPages > 5 && startPage > 1) {
              allElements.push(
                <button
                  key="first"
                  onClick={() => goToPage(1)}
                  disabled={loading}
                  className={`button px-3 py-2 border border-transparent disabled:opacity-50 ${paginationTextClass}`}
                  style={paginationInactiveColour ? { color: paginationInactiveColour } : {}}
                >
                  1
                </button>
              );

              if (startPage > 2) {
                allElements.push(
                  <span
                    key="first-ellipsis"
                    className={`px-2 ${paginationTextClass}`}
                    style={paginationInactiveColour ? { color: paginationInactiveColour } : {}}
                  >
                    ...
                  </span>
                );
              }
            }

            // Main page buttons
            for (let pageNum = startPage; pageNum <= endPage; pageNum++) {
              const isActive = pageNum === currentPage;
              allElements.push(
                <button
                  key={pageNum}
                  onClick={() => goToPage(pageNum)}
                  disabled={loading}
                  className={`w-10 h-10 rounded-full ${
                    isActive
                      ? `border border-[#101010] ${paginationTextClass}`
                      : `border border-transparent ${paginationTextClass}`
                  } ${loading ? 'opacity-50 cursor-not-allowed' : ''}`}
                  aria-label={`Go to page ${pageNum}`}
                  aria-current={isActive ? 'page' : undefined}
                  style={
                    isActive
                      ? paginationActiveColour
                        ? { color: paginationActiveColour }
                        : {}
                      : paginationInactiveColour
                      ? { color: paginationInactiveColour }
                      : {}
                  }
                >
                  {loading && pageNum !== currentPage ? (
                    <div className='w-4 h-4 border-2 border-gray-300 border-t-primary rounded-full animate-spin'></div>
                  ) : (
                    pageNum
                  )}
                </button>
              );
            }

            // Last page and ellipsis
            if (totalPages > 5 && endPage < totalPages) {
              if (endPage < totalPages - 1) {
                allElements.push(
                  <span
                    key="last-ellipsis"
                    className={`px-2 text-gray-500 ${paginationTextClass}`}
                    style={paginationInactiveColour ? { color: paginationInactiveColour } : {}}
                  >
                    ...
                  </span>
                );
              }

              allElements.push(
                <button
                  key="last"
                  onClick={() => goToPage(totalPages)}
                  disabled={loading}
                  className={`button border border-transparent disabled:opacity-50 ${paginationTextClass}`}
                  style={paginationInactiveColour ? { color: paginationInactiveColour } : {}}
                >
                  {totalPages}
                </button>
              );
            }

            return allElements;
          })()}
        </div>
        <button
          onClick={() => goToPage(currentPage + 1)}
          disabled={currentPage >= totalPages || loading}
          className={` disabled:opacity-50 disabled:cursor-not-allowed ${paginationTextClass}`}
          aria-label='Next page'
          style={
            (currentPage >= totalPages || loading) && paginationInactiveColour
              ? { color: paginationInactiveColour }
              : undefined
          }
        >
          <i className='icon icon-chevron-right text-[24px]'></i>
        </button>
      </div>
    );
  }
  // Load more button
  return (
    !isActuallyLastPage && (
      <button
        onClick={loadMoreProducts}
        disabled={loading}
        className={`button button-primary flex items-center ${paginationTextClass}`}
        aria-label={`Load page ${currentPage + 1}`}
        style={paginationInactiveColour ? { color: paginationInactiveColour } : {}}
      >
        Load More
      </button>
    )
  );
};

ProductListPagination.defaultProps = {
  paginationTextClass: '',
  paginationActiveColour: '',
  paginationInactiveColour: ''
};

export default ProductListPagination;
